import React from 'react';
import ActionsDropdown from '../ActionsDropdown';
import styles from '../../styles/components/survey-stack/SurveyItem.module.css';
import { TimestampMark } from 'components/TimestampMark';
import SurveyStatus from '@ax2/common/model/SurveyStatus';
import { SurveyButton } from './SurveyButton';
import editSurveyIcon from '../../assets/edit_mirrored.svg';
import sendIcon from '../../assets/send.svg';
import chartIcon from '../../assets/chart.svg';
import PanelType, { getPanelTypeText } from '@ax2/common/model/PanelType';
import SurveySchema from '@ax2/common/model/schemas/SurveySchema';
import { usePdfDownload } from 'helpers/hooks/usePdfDownload';

export interface ItemData {
  surveySchema: SurveySchema;
  name: string | null;
  createdAt: Date;
  updatedAt: Date;
  status: SurveyStatus;
  panelName?: PanelType;
  onEdit: () => void;
  onPreview: () => void;
  onResults: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export const SurveyItem: React.FC<ItemData> = ({ surveySchema, ...props }) => {
  const { pdfDownload } = usePdfDownload(surveySchema);

  const title =
    props.status === SurveyStatus.ReadyToSend
      ? 'Draft'
      : props.status.toString();
  const subtitle =
    props.status === SurveyStatus.ReadyToSend ? 'Ready to send' : null;
  return (
    <div className={`${styles.itemContainer}`}>
      <div className={styles.infoContainer}>
        <div
          className={
            props.status === SurveyStatus.ReadyToSend
              ? styles.activeStateInfoContainer
              : styles.draftStateInfoContainer
          }
        >
          <span className={styles.statusTitle}>{title}</span>
          {subtitle && (
            <>
              <span className={styles.statusSubTitle}>&nbsp;—&nbsp;</span>
              <span className={styles.statusSubTitle}>{subtitle}</span>
            </>
          )}
        </div>
        <div className={styles.surveyNameWrapper}>
          <div className={styles.surveyNameContainer}>
            <span className={styles.surveyName}>
              {props.name ? props.name : 'Unspecified'}
            </span>
            {props.panelName && (
              <span className={styles.panelNameText}>{`For ${getPanelTypeText(
                props.panelName
              )} Panel`}</span>
            )}
          </div>
        </div>
        <div>
          <TimestampMark
            createdAt={props.createdAt}
            modifiedAt={props.updatedAt}
          />
        </div>
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.buttonsContainer}>
          <SurveyButton
            text="Edit survey"
            imageSource={editSurveyIcon}
            isDisabled={
              props.status !== SurveyStatus.Draft &&
              props.status !== SurveyStatus.ReadyToSend
            }
            onClick={props.onEdit}
          />
          <SurveyButton
            text="Preview & send"
            imageSource={sendIcon}
            isDisabled={props.status !== SurveyStatus.ReadyToSend}
            onClick={props.onPreview}
          />
          <SurveyButton
            text="Results"
            imageSource={chartIcon}
            isDisabled={
              props.status !== SurveyStatus.Active &&
              props.status !== SurveyStatus.Closed
            }
            onClick={props.onResults}
          />
        </div>
        <div className={styles.actionContainer}>
          <ActionsDropdown
            actions={[
              {
                text: 'Download',
                onCompletion: pdfDownload,
              },
              {
                text: 'Duplicate',
                onCompletion: props.onDuplicate,
              },
              {
                text: 'Delete',
                onCompletion: props.onDelete,
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};
